import { apiClient } from './apiClient';

/**
 * Admin data-migration runs. Super-admin only; every endpoint is behind
 * EnsureSuperAdmin server-side.
 *
 * The API never returns a stored API key — `hasStoredCredentials` only says
 * whether one is still held, and it is purged when a run reaches a terminal
 * state.
 */

export type ImportRunStatus =
  | 'pending'
  | 'fetching'
  | 'preview_ready'
  | 'committing'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** Commit dependency order: later entities resolve references the earlier ones registered. */
export const IMPORT_ENTITIES = ['service', 'employee', 'guest', 'appointment', 'sale'] as const;

export type ImportEntity = (typeof IMPORT_ENTITIES)[number];

export const IMPORT_ENTITY_LABELS: Record<ImportEntity, string> = {
  service: 'Services',
  employee: 'Providers (as resources)',
  guest: 'Guests (as patients)',
  appointment: 'Appointments',
  sale: 'Sales history',
};

/** Entities whose Zenoti endpoints are date-ranged, so a range is required. */
export const DATE_RANGED_ENTITIES: ImportEntity[] = ['appointment', 'sale'];

export interface ImportRunCounts {
  planned?: Record<string, Record<string, number>>;
  outcome?: Record<string, Record<string, number>>;
  skipReasons?: Record<string, number>;
}

export interface ImportRunSummary {
  importRunId: number;
  source: string;
  accountId: number;
  accountName: string;
  status: ImportRunStatus;
  entities: ImportEntity[];
  dateFrom: string | null;
  dateTo: string | null;
  centerFacilityMap: Record<string, number>;
  progress: string | null;
  counts: ImportRunCounts | null;
  error: string | null;
  hasStoredCredentials: boolean;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
}

export interface ImportPreviewItem {
  externalId: string;
  action: 'create' | 'link' | 'skip';
  matchedInternalId: string | null;
  matchNotes: string | null;
  skipReason: string | null;
  summary: string;
}

export interface ImportRunDetail extends ImportRunSummary {
  preview: Partial<Record<ImportEntity, ImportPreviewItem[]>> | null;
}

export interface ZenotiCenterOption {
  centerId: string;
  name: string;
  code: string;
}

export interface FacilityOption {
  facilityId: number;
  name: string;
}

export interface AccountOption {
  accountId: number;
  name: string;
}

export interface ImportReportItem {
  id: number;
  entityType: ImportEntity;
  externalId: string;
  action: string;
  status: string;
  matchedInternalId: string | null;
  matchNotes: string | null;
  resultInternalId: string | null;
  skipReason: string | null;
  error: string | null;
}

export interface CreateImportRunInput {
  accountID: number;
  apiKey: string;
  entities: ImportEntity[];
  dateFrom?: string | null;
  dateTo?: string | null;
}

export interface CreateImportRunResult {
  importRunId: number;
  status: ImportRunStatus;
  centers: ZenotiCenterOption[];
  facilities: FacilityOption[];
}

export function listImportRuns(): Promise<ImportRunSummary[]> {
  return apiClient.get('/import-runs').then((r) => r.data.data);
}

export function getImportRun(id: number): Promise<ImportRunDetail> {
  return apiClient.get(`/import-runs/${id}`).then((r) => r.data.data);
}

export function listImportAccounts(): Promise<AccountOption[]> {
  return apiClient.get('/import-runs/accounts').then((r) => r.data.data);
}

export function createImportRun(input: CreateImportRunInput): Promise<CreateImportRunResult> {
  return apiClient
    .post('/import-runs', { source: 'zenoti', ...input })
    .then((r) => r.data.data);
}

export function getImportRunCenters(id: number): Promise<{
  centers: ZenotiCenterOption[];
  facilities: FacilityOption[];
  centerFacilityMap: Record<string, number>;
}> {
  return apiClient.get(`/import-runs/${id}/centers`).then((r) => r.data.data);
}

export function updateImportRunMapping(
  id: number,
  centerFacilityMap: Record<string, number>,
): Promise<{ centerFacilityMap: Record<string, number> }> {
  return apiClient.put(`/import-runs/${id}/mapping`, { centerFacilityMap }).then((r) => r.data.data);
}

export function startImportFetch(id: number): Promise<{ status: ImportRunStatus }> {
  return apiClient.post(`/import-runs/${id}/fetch`).then((r) => r.data.data);
}

export function commitImportRun(id: number): Promise<{ status: ImportRunStatus }> {
  return apiClient.post(`/import-runs/${id}/commit`).then((r) => r.data.data);
}

export function cancelImportRun(id: number): Promise<{ status: ImportRunStatus }> {
  return apiClient.post(`/import-runs/${id}/cancel`).then((r) => r.data.data);
}

export function getImportRunReport(
  id: number,
  params: { entityType?: string; status?: string; page?: number; perPage?: number } = {},
): Promise<{ counts: ImportRunCounts | null; items: ImportReportItem[] }> {
  return apiClient.get(`/import-runs/${id}/report`, { params }).then((r) => r.data.data);
}

/**
 * A run in one of these states is still being worked on by the import worker,
 * so the UI keeps polling.
 */
export function isImportRunActive(status: ImportRunStatus): boolean {
  return status === 'fetching' || status === 'committing';
}

export function isImportRunTerminal(status: ImportRunStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export const IMPORT_STATUS_LABELS: Record<ImportRunStatus, string> = {
  pending: 'Awaiting setup',
  fetching: 'Fetching from Zenoti',
  preview_ready: 'Ready to review',
  committing: 'Writing records',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const SKIP_REASON_LABELS: Record<string, string> = {
  already_imported: 'Already imported by an earlier run',
  duplicate_credentials: 'Email or phone already belongs to another patient',
  unmapped_patient: 'Its guest was not imported, so there is no patient to attach to',
  unmapped_facility: 'Its Zenoti center is not mapped to a facility',
  unverified_mapping: 'Missing or unusable data in the Zenoti record',
};
